import { computed, effect, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { FuseConfigService } from '@fuse/services/config';
import {
  CreateExpertRequest,
  ExpertWorkspaceRecord,
  KeycloakRealm,
  PurchaseExpertWorkspaceSubscriptionPlanRequest,
  SwitchExpertWorkspaceSubscriptionPlanRequest,
  UpdateExpertRequest,
  UpdateExpertWorkspaceCodeRequest,
  UpdateExpertWorkspaceExpertBusinessHoursRequest,
  UpdateExpertWorkspaceExpertExcludedNotificationsRequest,
  UpdateExpertWorkspaceExpertScheduleReservationRequest,
  UpdateExpertWorkspaceImageRequest,
  UpdateExpertWorkspaceNameRequest,
  UpdateExpertWorkspaceNextSubscriptionPlanRequest,
  UpdateUsernameRequest,
  UpdateUserNicknameRequest,
  UserRole,
  VerifyExpertPhoneNumberRequest,
  WebColorScheme,
} from '@libs/shared/api';
import { DeleteWithTypeDialogService } from '@libs/shared/ui/dialogs/delete-with-type-dialog';
import { convertToFuseScheme } from '@libs/shared/utils/functions/convert-to-fuse-scheme';
import { AuthUserProfile } from '@sprint-expert/app/core/auth/auth-user-profile';
import { AppStatusCoreStore } from '@sprint-expert/app/core/state/store/app-status-core.store';
import { AuthUserProfileCoreStore } from '@sprint-expert/app/core/state/store/auth-user-profile-core.store';
import { CurrentExpertCoreStore } from '@sprint-expert/app/core/state/store/current-expert-core-store.service';
import { CurrentExpertWorkspaceCoreStore } from '@sprint-expert/app/core/state/store/current-expert-workspace-core-store.service';
import { CurrentUserCoreStore } from '@sprint-expert/app/core/state/store/current-user-core.store';
import {
  ExpertEventNotificationCoreQueryFilter,
  ExpertEventNotificationCoreStore,
} from '@sprint-expert/app/core/state/store/expert-event-notification-core.store';
import { NavigationCoreStore } from '@sprint-expert/app/core/state/store/navigation-core-store.service';
import { hasExpertWorkspaceActiveSubscription } from '@sprint-expert/app/utils/functions/expert-workspace.utils';

@Injectable({
  providedIn: 'root',
})
export class CoreService {
  private readonly authUserProfileCoreStore = inject(AuthUserProfileCoreStore);
  private readonly currentUserCoreStore = inject(CurrentUserCoreStore);
  private readonly currentExpertCoreStore = inject(CurrentExpertCoreStore);
  private readonly currentExpertWorkspaceCoreStore = inject(
    CurrentExpertWorkspaceCoreStore
  );
  private readonly appStatusCoreStore = inject(AppStatusCoreStore);
  private readonly navigationCoreStore = inject(NavigationCoreStore);
  private readonly eventNotificationCoreStore = inject(
    ExpertEventNotificationCoreStore
  );
  private readonly router = inject(Router);
  private readonly fuseConfigService = inject(FuseConfigService);
  private readonly deleteWithTypeDialog = inject(DeleteWithTypeDialogService);

  authUserProfile$$ = this.authUserProfileCoreStore.content$$;
  authUserProfileIsLoading$$ = this.authUserProfileCoreStore.isLoading$$;
  authUserProfileIsLoaded$$ = this.authUserProfileCoreStore.isLoaded$$;
  authUserProfileErrorMessage$$ = this.authUserProfileCoreStore.errorMessage$$;
  currentUserRole$$ = computed(() => {
    if (this.authUserProfile$$() && this.authUserProfileIsLoaded$$()) {
      const roles = this.authUserProfile$$().info.realm_access.roles;
      if (roles.includes('ROLE_EXPERT')) {
        return UserRole.Expert;
      } else if (roles.includes('ROLE_PREP_EXPERT')) {
        return UserRole.PrepExpert;
      }
    }
    return UserRole.PrepExpert;
  });

  currentUserId$$ = computed(() => {
    if (this.authUserProfile$$() && this.authUserProfileIsLoaded$$()) {
      return this.authUserProfile$$().info.sub;
    }
  });
  currentUser$$ = this.currentUserCoreStore.content$$;
  currentUserIsLoading$$ = this.currentUserCoreStore.isLoading$$;
  currentUserIsLoaded$$ = this.currentUserCoreStore.isLoaded$$;
  currentUserErrorMessage$$ = this.currentUserCoreStore.errorMessage$$;

  currentExpert$$ = this.currentExpertCoreStore.content$$;
  currentExpertIsLoading$$ = this.currentExpertCoreStore.isLoading$$;
  currentExpertIsLoaded$$ = this.currentExpertCoreStore.isLoaded$$;
  currentExpertErrorMessage$$ = this.currentExpertCoreStore.errorMessage$$;

  currentExpertWorkspace$$ = this.currentExpertWorkspaceCoreStore.content$$;
  currentExpertWorkspaceIsLoading$$ =
    this.currentExpertWorkspaceCoreStore.isLoading$$;
  currentExpertWorkspaceIsLoaded$$ =
    this.currentExpertWorkspaceCoreStore.isLoaded$$;
  currentExpertWorkspaceErrorMessage$$ =
    this.currentExpertWorkspaceCoreStore.errorMessage$$;

  currentExpertWorkspaceExpert$$ = computed(() => {
    if (
      this.currentExpertWorkspace$$() &&
      this.currentExpertWorkspaceIsLoaded$$()
    ) {
      return this.currentExpertWorkspace$$().workspaceExperts.find(
        participant => participant.expert.id === this.currentUserId$$()
      );
    }
  });

  hasExpertWorkspaceActiveSubscription$$ = computed(() =>
    hasExpertWorkspaceActiveSubscription(this.currentExpertWorkspace$$())
  );

  appStatus$$ = this.appStatusCoreStore.content$$;
  appStatusIsLoading$$ = this.appStatusCoreStore.isLoading$$;
  appStatusIsLoaded$$ = this.appStatusCoreStore.isLoaded$$;
  appStatusErrorMessage$$ = this.appStatusCoreStore.errorMessage$$;

  navigation$$ = this.navigationCoreStore.content$$;
  navigationIsLoading$$ = this.navigationCoreStore.isLoading$$;
  navigationIsLoaded$$ = this.navigationCoreStore.isLoaded$$;
  navigationErrorMessage$$ = this.navigationCoreStore.errorMessage$$;

  eventNotificationSlice$$ = this.eventNotificationCoreStore.slice$$;
  eventNotificationPageQuery$$ = this.eventNotificationCoreStore.pageQuery$$;
  eventNotificationIsLoading$$ = this.eventNotificationCoreStore.isLoading$$;
  eventNotificationIsLoaded$$ = this.eventNotificationCoreStore.isLoaded$$;
  eventNotificationErrorMessage$$ =
    this.eventNotificationCoreStore.errorMessage$$;
  eventNotificationUnreadCount$$ =
    this.eventNotificationCoreStore.unreadCount$$;

  expertCreated$ = this.currentExpertCoreStore.created$;
  expertUpdated$ = this.currentExpertCoreStore.updated$;

  constructor() {
    effect(
      () => {
        if (this.currentExpert$$()) {
          this.fuseConfigService.config = {
            scheme: convertToFuseScheme(this.currentExpert$$().webColorScheme),
          };

          if (this.currentExpert$$().phoneNumberVerified === false) {
            this.router.navigateByUrl('/phone-number-verification');
            return;
          }

          return;
        }
      },
      {
        allowSignalWrites: true,
      }
    );

    effect(
      () => {
        if (this.currentExpertWorkspace$$()) {
          if (this.currentExpertWorkspaceExpert$$()) {
            this.navigationCoreStore.fetchNavigation({
              expertWorkspace: this.currentExpertWorkspace$$(),
              workspaceExpertRole: this.currentExpertWorkspaceExpert$$().role,
            });
          } else {
            this.router.navigate(['/']);
          }

          if (!this.currentExpertWorkspace$$().active) {
            this.router.navigate(['expert-workspace-selection']);
            return;
          }
          if (this.hasExpertWorkspaceActiveSubscription$$() === false) {
            if (this.currentExpertWorkspaceExpert$$().role === 'Owner') {
              this.router.navigate([
                'expert-workspace',
                this.currentExpertWorkspace$$().id,
                'expert-workspace-subscription-plan-purchase',
              ]);
              return;
            } else {
              this.router.navigate([
                'expert-workspace',
                this.currentExpertWorkspace$$().id,
                'expert-workspace-no-active-subscription',
              ]);
              return;
            }
          }

          if (
            this.currentExpertWorkspace$$().activeSubscriptionPlanType ===
              'Personal' &&
            !(this.currentExpertWorkspaceExpert$$().role === 'Owner')
          ) {
            this.router.navigate([
              'expert-workspace',
              this.currentExpertWorkspace$$().id,
              'expert-workspace-no-team-subscription',
            ]);
            return;
          }
        }
      },
      {
        allowSignalWrites: true,
      }
    );
    effect(
      () => {
        if (this.currentExpertWorkspaceExpert$$()) {
          if (
            this.currentExpertWorkspaceExpert$$().joinStatusType === 'Pending'
          ) {
            this.router.navigate([
              'expert-workspace',
              this.currentExpertWorkspace$$().id,
              'expert-workspace-pending-join-inquiry',
            ]);
          }
        }
      },
      {
        allowSignalWrites: true,
      }
    );

    effect(
      () => {
        if (this.authUserProfile$$()) {
          this.currentUserCoreStore.createUserByAuthUserProfile({
            authUserProfile: this.authUserProfile$$(),
            realm: KeycloakRealm.SprintExpertApp,
          });

          this.currentExpertCoreStore.fetchExpertById({
            id: this.authUserProfile$$().info.sub,
          });
        }
      },
      {
        allowSignalWrites: true,
      }
    );
  }

  enter() {
    this.fetchAppStatus();
  }

  setExpertWorkspace(value: ExpertWorkspaceRecord) {
    this.currentExpertWorkspaceCoreStore.setExpertWorkspace(value);
  }

  fetchAppStatus() {
    this.appStatusCoreStore.fetchAppStatus();
  }

  setAuthUser(authUserProfile: AuthUserProfile) {
    this.authUserProfileCoreStore.setAuthUserProfile(authUserProfile);
  }

  /// Expert
  fetchCurrentExpert() {
    this.currentExpertCoreStore.fetchExpertById({
      id: this.authUserProfile$$().info.sub,
    });
  }

  createCurrentExpert(request: CreateExpertRequest) {
    this.currentExpertCoreStore.createExpert({
      request,
      showSuccessSnackbar: true,
      showFailureSnackbar: true,
    });
  }

  updateCurrentExpert(request: UpdateExpertRequest) {
    this.currentExpertCoreStore.updateExpert({
      id: this.currentExpert$$().id,
      request,
      showSuccessSnackbar: true,
      showFailureSnackbar: true,
    });
  }

  updateWebColorScheme(webColorScheme: WebColorScheme) {
    this.currentExpertCoreStore.updateExpertWebColorScheme({
      id: this.currentExpert$$().id,
      webColorScheme,
      showSuccessSnackbar: true,
      showFailureSnackbar: true,
    });
  }

  verifyExpertPhoneNumber(request: VerifyExpertPhoneNumberRequest) {
    this.currentExpertCoreStore.verifyExpertPhoneNumber({
      id: this.currentExpert$$().id,
      request,
      showSuccessSnackbar: true,
      showFailureSnackbar: true,
    });
  }

  /// ExpertWorkspace
  updateExpertWorkspaceName(request: UpdateExpertWorkspaceNameRequest) {
    this.currentExpertWorkspaceCoreStore.updateName({
      id: this.currentExpertWorkspace$$().id,
      request,
      showSuccessSnackbar: true,
      showFailureSnackbar: true,
    });
  }

  updateExpertWorkspaceCode(request: UpdateExpertWorkspaceCodeRequest) {
    this.currentExpertWorkspaceCoreStore.updateCode({
      id: this.currentExpertWorkspace$$().id,
      request,
      showSuccessSnackbar: true,
      showFailureSnackbar: true,
    });
  }

  updateExpertWorkspaceImage(request: UpdateExpertWorkspaceImageRequest) {
    this.currentExpertWorkspaceCoreStore.updateImage({
      id: this.currentExpertWorkspace$$().id,
      request,
      showSuccessSnackbar: true,
      showFailureSnackbar: true,
    });
  }

  openExpertWorkspaceExpertJoinInquiry() {
    this.currentExpertWorkspaceCoreStore.openExpertWorkspaceExpertJoinInquiry({
      id: this.currentExpertWorkspace$$().id,
      showSuccessSnackbar: true,
      showFailureSnackbar: true,
    });
  }

  acceptExpertWorkspaceExpertJoinInquiry(expertWorkspaceExpertId: string) {
    this.currentExpertWorkspaceCoreStore.acceptExpertWorkspaceExpertJoinInquiry(
      {
        id: this.currentExpertWorkspace$$().id,
        expertWorkspaceExpertId,
        showSuccessSnackbar: true,
        showFailureSnackbar: true,
      }
    );
  }

  declineExpertWorkspaceExpertJoinInquiry(expertWorkspaceExpertId: string) {
    this.currentExpertWorkspaceCoreStore.declineExpertWorkspaceExpertJoinInquiry(
      {
        id: this.currentExpertWorkspace$$().id,
        expertWorkspaceExpertId,
        showSuccessSnackbar: true,
        showFailureSnackbar: true,
      }
    );
  }

  removeExpertWorkspaceExpert(expertWorkspaceExpertId: string) {
    this.currentExpertWorkspaceCoreStore.removeExpertWorkspaceExpert({
      id: this.currentExpertWorkspace$$().id,
      expertWorkspaceExpertId,
      showSuccessSnackbar: true,
      showFailureSnackbar: true,
    });
  }

  updateExpertWorkspaceExpertBusinessHours(
    request: UpdateExpertWorkspaceExpertBusinessHoursRequest
  ) {
    this.currentExpertWorkspaceCoreStore.updateExpertWorkspaceExpertBusinessHours(
      {
        id: this.currentExpertWorkspace$$().id,
        expertWorkspaceExpertId: this.currentExpertWorkspaceExpert$$().id,
        request,
        showSuccessSnackbar: true,
        showFailureSnackbar: true,
      }
    );
  }

  enableExpertWorkspaceExpertSessionSignature() {
    this.currentExpertWorkspaceCoreStore.enableExpertWorkspaceExpertSessionSignature(
      {
        id: this.currentExpertWorkspace$$().id,
        expertWorkspaceExpertId: this.currentExpertWorkspaceExpert$$().id,
        showSuccessSnackbar: true,
        showFailureSnackbar: true,
      }
    );
  }

  disableExpertWorkspaceExpertSessionSignature() {
    this.currentExpertWorkspaceCoreStore.disableExpertWorkspaceExpertSessionSignature(
      {
        id: this.currentExpertWorkspace$$().id,
        expertWorkspaceExpertId: this.currentExpertWorkspaceExpert$$().id,
        showSuccessSnackbar: true,
        showFailureSnackbar: true,
      }
    );
  }

  enableExpertWorkspaceAttendanceKiosk() {
    this.currentExpertWorkspaceCoreStore.enableExpertWorkspaceAttendanceKiosk({
      id: this.currentExpertWorkspace$$().id,
      showSuccessSnackbar: true,
      showFailureSnackbar: true,
    });
  }

  disableExpertWorkspaceAttendanceKiosk() {
    this.currentExpertWorkspaceCoreStore.disableExpertWorkspaceAttendanceKiosk({
      id: this.currentExpertWorkspace$$().id,
      showSuccessSnackbar: true,
      showFailureSnackbar: true,
    });
  }

  updateExpertWorkspaceExpertExcludedNotifications(
    request: UpdateExpertWorkspaceExpertExcludedNotificationsRequest
  ) {
    this.currentExpertWorkspaceCoreStore.updateExpertWorkspaceExpertExcludedNotifications(
      {
        id: this.currentExpertWorkspace$$().id,
        expertWorkspaceExpertId: this.currentExpertWorkspaceExpert$$().id,
        request,
        showSuccessSnackbar: true,
        showFailureSnackbar: true,
      }
    );
  }

  updateExpertWorkspaceExpertScheduleReservation(
    request: UpdateExpertWorkspaceExpertScheduleReservationRequest
  ) {
    this.currentExpertWorkspaceCoreStore.updateExpertWorkspaceExpertScheduleReservation(
      {
        id: this.currentExpertWorkspace$$().id,
        expertWorkspaceExpertId: this.currentExpertWorkspaceExpert$$().id,
        request,
        showSuccessSnackbar: true,
        showFailureSnackbar: true,
      }
    );
  }

  purchaseExpertWorkspaceSubscriptionPlan(
    request: PurchaseExpertWorkspaceSubscriptionPlanRequest
  ) {
    this.currentExpertWorkspaceCoreStore.purchaseExpertWorkspaceSubscriptionPlan(
      {
        id: this.currentExpertWorkspace$$().id,
        request,
        showSuccessSnackbar: true,
        showFailureSnackbar: true,
      }
    );
  }

  switchExpertWorkspaceSubscriptionPlan(
    request: SwitchExpertWorkspaceSubscriptionPlanRequest
  ) {
    this.currentExpertWorkspaceCoreStore.switchExpertWorkspaceSubscriptionPlan({
      id: this.currentExpertWorkspace$$().id,
      request,
      showSuccessSnackbar: true,
      showFailureSnackbar: true,
    });
  }

  updateExpertWorkspaceNextSubscriptionPlan(
    request: UpdateExpertWorkspaceNextSubscriptionPlanRequest
  ) {
    this.currentExpertWorkspaceCoreStore.updateExpertWorkspaceNextSubscriptionPlan(
      {
        id: this.currentExpertWorkspace$$().id,
        request,
        showSuccessSnackbar: true,
        showFailureSnackbar: true,
      }
    );
  }

  cancelExpertWorkspaceNextSubscriptionPlan() {
    this.currentExpertWorkspaceCoreStore.cancelExpertWorkspaceNextSubscriptionPlan(
      {
        id: this.currentExpertWorkspace$$().id,
        showSuccessSnackbar: true,
        showFailureSnackbar: true,
      }
    );
  }

  terminateCurrentExpertWorkspace() {
    const dialogRef = this.deleteWithTypeDialog.open({
      title: '센터를 삭제하시겠어요?',
      message:
        '센터를 삭제하는 경우, 모든 데이터는 삭제되며, 복구할 수 없어요.',
      text: this.currentExpertWorkspace$$().name,
      placeholder: '위의 이름을 동일하게 입력해주세요',
      submitText: '삭제하기',
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.currentExpertWorkspaceCoreStore.terminateExpertWorkspace({
          id: this.currentExpertWorkspace$$().id,
          showSuccessSnackbar: true,
          showFailureSnackbar: true,
        });
      }
    });
  }

  // User
  updateCurrentUserNickname(request: UpdateUserNicknameRequest) {
    this.currentUserCoreStore.updateUserNickname({
      id: this.currentUser$$().id,
      request,
      showSuccessSnackbar: true,
      showFailureSnackbar: true,
    });
  }

  updateCurrentUsername(request: UpdateUsernameRequest) {
    this.currentUserCoreStore.updateUsername({
      id: this.currentUser$$().id,
      request,
      showSuccessSnackbar: true,
      showFailureSnackbar: true,
    });
  }

  updateCurrentUserProfileImage(imageFile: Blob, contentType: string) {
    this.currentUserCoreStore.updateUserProfileImage({
      id: this.currentUser$$().id,
      imageFile,
      contentType,
      showSuccessSnackbar: true,
      showFailureSnackbar: true,
    });
  }

  deleteCurrentUserProfileImage() {
    this.currentUserCoreStore.deleteUserProfileImage({
      id: this.currentUser$$().id,
      showSuccessSnackbar: true,
      showFailureSnackbar: true,
    });
  }

  // ExpertEventNotification
  fetchExpertEventNotificationSlice() {
    this.eventNotificationCoreStore.fetchExpertEventNotificationSlice({
      expertWorkspaceId: this.currentExpertWorkspace$$().id,
      active: true,
    });
  }

  fetchNextExpertEventNotificationSlice() {
    this.eventNotificationCoreStore.fetchNextExpertEventNotificationSlice();
  }

  fetchExpertEventNotificationById(id: string) {
    this.eventNotificationCoreStore.fetchExpertEventNotificationById({
      id,
    });
  }

  fetchExpertEventNotificationUnreadCount() {
    this.eventNotificationCoreStore.fetchExpertEventNotificationUnreadCount({
      expertWorkspaceId: this.currentExpertWorkspace$$().id,
      active: true,
    });
  }

  activateExpertEventNotification(id: string) {
    this.eventNotificationCoreStore.activateExpertEventNotification({
      id,
    });
  }

  inactivateExpertEventNotification(id: string) {
    this.eventNotificationCoreStore.inactivateExpertEventNotification({
      id,
    });
  }

  readExpertEventNotification(id: string) {
    this.eventNotificationCoreStore.readExpertEventNotification({
      id,
    });
  }

  unreadExpertEventNotification(id: string) {
    this.eventNotificationCoreStore.unreadExpertEventNotification({
      id,
    });
  }

  readAllExpertEventNotifications(notifierId: string) {
    this.eventNotificationCoreStore.readAllExpertEventNotifications({
      request: {
        notifierId,
      },
    });
  }

  updateExpertEventNotificationPageQuery(
    pageQuery: ExpertEventNotificationCoreQueryFilter
  ) {
    this.eventNotificationCoreStore.updateExpertEventNotificationPageQuery(
      pageQuery
    );
  }
}

export function injectCoreFeature() {
  return inject(CoreService);
}
